import { makeStyles } from '@material-ui/core/styles'
import { pageContentHorizontalGutters } from 'lib/styles/page-content-gutters'

const STATUS_TONES = {
  DRAFT: { backgroundColor: '#F4F6F8', color: '#4F5B62' },
  SCHEDULED: { backgroundColor: '#E0EAFF', color: '#001C57' },
  SENT: { backgroundColor: '#BEF1BB', color: '#00210B' },
  CANCELED: { backgroundColor: '#F4F6F8', color: '#6B7C87' },
}

const useStyles = makeStyles(theme => ({
  pageContent: {
    ...pageContentHorizontalGutters(theme),
    margin: theme.spacing(0, 0, 12),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(3),
  },
  content: {
    margin: '2rem auto 6rem',
    maxWidth: 832,
    padding: theme.spacing(0, 2),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(3),
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(0, 1),
      marginTop: theme.spacing(2),
    },
  },
  card: {
    border: '1px solid #E0E0E0',
    borderRadius: 12,
    backgroundColor: theme.palette.background.paper,
    overflow: 'hidden',
  },
  cardHeader: {
    padding: theme.spacing(2, 2.5),
    borderBottom: '1px solid #E0E0E0',
  },
  audienceCountLabel: {
    fontSize: '1rem',
    fontWeight: 700,
    lineHeight: 1.3,
    color: '#172024',
  },
  cardSubtitle: {
    marginTop: theme.spacing(0.5),
    fontSize: '0.875rem',
    lineHeight: 1.5,
    color: theme.palette.text.secondary,
  },
  cardBody: {
    padding: theme.spacing(2.5),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2.5),
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(2),
    },
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    padding: theme.spacing(2, 2.5),
    textAlign: 'left',
    textDecoration: 'none',
    color: 'inherit',
    cursor: 'pointer',
    '&:not(:last-child)': {
      borderBottom: '1px solid #E0E0E0',
    },
    '&:hover': {
      backgroundColor: '#FAFBFC',
    },
  },
  rowBody: {
    flex: 1,
    minWidth: 0,
  },
  rowTitle: {
    fontSize: '0.9375rem',
    fontWeight: 600,
    lineHeight: 1.3,
    color: '#172024',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  rowDetail: {
    marginTop: theme.spacing(0.5),
    fontSize: '0.8125rem',
    lineHeight: 1.4,
    color: theme.palette.text.secondary,
  },
  rowStats: {
    display: 'flex',
    gap: theme.spacing(2.5),
    flexShrink: 0,
    [theme.breakpoints.down('xs')]: {
      display: 'none',
    },
  },
  rowStat: {
    textAlign: 'right',
  },
  rowStatValue: {
    fontSize: '0.9375rem',
    fontWeight: 700,
    color: '#172024',
  },
  rowStatLabel: {
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
  },
  statusBadge: ({ status }) => ({
    display: 'inline-block',
    flexShrink: 0,
    fontFamily: theme.typography.fontFamily,
    padding: theme.spacing(0.25, 1),
    borderRadius: 12,
    fontSize: '0.75rem',
    fontWeight: 600,
    lineHeight: 1.5,
    whiteSpace: 'nowrap',
    ...(STATUS_TONES[status] || STATUS_TONES.DRAFT),
  }),
  emptyState: {
    padding: theme.spacing(4, 2.5),
    textAlign: 'center',
  },
  emptyTitle: {
    fontSize: '1rem',
    fontWeight: 700,
    color: '#172024',
    marginBottom: theme.spacing(1),
  },
  emptyCopy: {
    fontSize: '0.875rem',
    lineHeight: 1.5,
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(2),
  },
  pillButton: {
    borderRadius: 20,
    fontWeight: 700,
    whiteSpace: 'nowrap',
    boxShadow: 'none',
    '&:hover': {
      boxShadow: 'none',
    },
  },
  stepper: {
    padding: theme.spacing(0, 0, 1),
    backgroundColor: 'transparent',
  },
  fieldGroupLabel: {
    fontSize: '0.875rem',
    fontWeight: 600,
    color: '#172024',
    marginBottom: theme.spacing(1),
  },
  helperText: {
    fontSize: '0.8125rem',
    lineHeight: 1.5,
    color: theme.palette.text.secondary,
  },
  chips: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
  },
  chipsHelper: {
    marginTop: theme.spacing(1),
  },
  audienceSummary: {
    display: 'flex',
    alignItems: 'baseline',
    gap: theme.spacing(1),
  },
  audienceCount: {
    fontSize: '1.5rem',
    fontWeight: 700,
    color: '#00205B',
  },
  previewList: {
    maxHeight: 320,
    overflowY: 'auto',
    border: '1px solid #E0E0E0',
    borderRadius: 8,
  },
  previewRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(0.5, 1.5),
    '&:not(:last-child)': {
      borderBottom: '1px solid #F0F0F0',
    },
  },
  previewName: {
    fontSize: '0.875rem',
    color: '#172024',
  },
  excluded: {
    color: theme.palette.text.disabled,
    textDecoration: 'line-through',
  },
  mediaGrid: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1.5),
  },
  mediaTile: {
    position: 'relative',
    width: 112,
    height: 112,
    borderRadius: 8,
    border: '1px solid #E0E0E0',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(0.5),
    padding: theme.spacing(1),
    backgroundColor: '#FAFBFC',
    textAlign: 'center',
  },
  mediaImage: {
    position: 'absolute',
    inset: 0,
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  mediaTitle: {
    fontSize: '0.75rem',
    lineHeight: 1.3,
    color: '#172024',
    overflow: 'hidden',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    wordBreak: 'break-word',
  },
  mediaRemove: {
    position: 'absolute',
    top: 4,
    right: 4,
    padding: 2,
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
    '&:hover': {
      backgroundColor: '#FFFFFF',
    },
  },
  mediaActions: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
  },
  footerActions: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: theme.spacing(1),
    flexWrap: 'wrap',
  },
  footerRight: {
    display: 'flex',
    gap: theme.spacing(1),
    marginLeft: 'auto',
  },
  chatPreview: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    gap: theme.spacing(1),
    padding: theme.spacing(2),
    backgroundColor: '#F4F6F8',
    borderRadius: 12,
  },
  chatBubble: {
    maxWidth: 420,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    padding: theme.spacing(1.25, 1.75),
    borderRadius: '16px 16px 4px 16px',
    backgroundColor: '#00205B',
    color: '#FFFFFF',
    fontSize: '0.875rem',
    lineHeight: 1.5,
  },
  chatCard: {
    width: 280,
    padding: theme.spacing(1.5),
    borderRadius: 12,
    border: '1px solid #E0E0E0',
    backgroundColor: '#FFFFFF',
  },
  chatCardTitle: {
    fontSize: '0.875rem',
    fontWeight: 600,
    color: '#172024',
  },
  chatCardAction: {
    marginTop: theme.spacing(0.75),
    fontSize: '0.8125rem',
    fontWeight: 600,
    color: theme.palette.primary.main,
  },
  chatImage: {
    width: 200,
    borderRadius: 12,
    display: 'block',
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
    gap: theme.spacing(1.5),
    [theme.breakpoints.down('xs')]: {
      gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    },
  },
  statCard: {
    padding: theme.spacing(1.75, 2),
    border: '1px solid #E0E0E0',
    borderRadius: 12,
    backgroundColor: theme.palette.background.paper,
  },
  statValue: {
    fontSize: '1.5rem',
    fontWeight: 700,
    lineHeight: 1.2,
    color: '#00205B',
  },
  statLabel: {
    marginTop: theme.spacing(0.5),
    fontSize: '0.8125rem',
    fontWeight: 600,
    color: '#172024',
  },
  statCaption: {
    marginTop: theme.spacing(0.25),
    fontSize: '0.75rem',
    lineHeight: 1.4,
    color: theme.palette.text.secondary,
  },
  directTone: {
    borderColor: '#9FD89B',
    backgroundColor: '#F2FBF1',
  },
  influencedTone: {
    borderColor: '#C5D4FF',
    backgroundColor: '#F4F7FF',
  },
  footnote: {
    fontSize: '0.75rem',
    lineHeight: 1.5,
    color: theme.palette.text.secondary,
  },
  table: {
    '& th': {
      fontSize: '0.75rem',
      fontWeight: 600,
      color: theme.palette.text.secondary,
      whiteSpace: 'nowrap',
    },
    '& td': {
      fontSize: '0.8125rem',
    },
  },
  kindBadge: ({ kind }) => ({
    display: 'inline-block',
    fontFamily: theme.typography.fontFamily,
    whiteSpace: 'nowrap',
    padding: theme.spacing(0.25, 1),
    borderRadius: 12,
    fontSize: '0.75rem',
    fontWeight: 600,
    ...(kind === 'DIRECT'
      ? { backgroundColor: '#BEF1BB', color: '#00210B' }
      : { backgroundColor: '#E0EAFF', color: '#001C57' }),
  }),
}))

export default useStyles
